/**
 * /me/travel/history — Session 3d PR 27.
 * Owning agent: Khidmat.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3dApi } from "../lib/session3d-api";
import type { TravelTripItem } from "../lib/session3d-api";
import { MeListPage } from "./_MeListPage";
import type { MeColumn } from "./_MeListPage";
import { useCurrentEmployeeId } from "./_session3dShell";

export function MyTravelHistoryPage() {
  const { t } = useTranslation();
  const employeeId = useCurrentEmployeeId();

  const query = useQuery({
    queryKey: ["s3d-travel-history", employeeId],
    queryFn: () => session3dApi.listMyTrips(employeeId),
    enabled: !!employeeId,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: MeColumn<TravelTripItem>[] = [
    {
      headerKey: "me.travel-history.col.destination",
      label: "Destination",
      render: (r) => <span className="font-medium">{r.destination}</span>,
    },
    {
      headerKey: "me.travel-history.col.dates",
      label: "Dates",
      render: (r) => <span dir="ltr" className="font-mono text-xs">{r.departure_date} → {r.return_date}</span>,
    },
    {
      headerKey: "me.travel-history.col.purpose",
      label: "Purpose",
      render: (r) => <span className="text-xs text-text-secondary">{r.purpose}</span>,
    },
    {
      headerKey: "me.travel-history.col.per_diem",
      label: "Per-diem (SAR)",
      render: (r) => <span className="font-mono text-xs">{(r.per_diem_received_sar ?? 0).toFixed(2)}</span>,
    },
  ];

  return (
    <MeListPage<TravelTripItem>
      title={t("me.travel-history.title", "My Past Trips")}
      subtitle={t("me.travel-history.subtitle", "Past travel records for personal reference.")}
      domain="travel-history"
      khidmatPrefill={t("me.travel-history.ask_khidmat", "Show me my travel history.")}
      query={query}
      columns={columns}
      rowKey={(r) => r.trip_id}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("me.travel-history.title", "My Past Trips"),
        window_label: t("me.travel-history.window", "past trips"),
      }}
      emptyHeadline={t("me.travel-history.empty_headline", "No past trips.")}
      emptyBody={t("me.travel-history.empty_body", "Khidmat: once you complete a trip, it appears here.")}
    />
  );
}
